<script>
  import { onMount } from 'svelte';
  import { scale } from 'svelte/transition';
  import { sortBy, shuffle } from 'lodash';
  import { detected_fonts, user_subscription } from './stores';
  import { getStorefrontByHash } from './services/storefronts';
  import { getUserSubscription } from './services/user';
  import Icons from './components/Icons.svelte';
  import Blink from './elements/Blink.svelte';
  import Button from './elements/Button.svelte';
  import StorefrontFontImagePreview from './components/StorefrontFontImagePreview.svelte';
  import StorefrontTextfield from './components/StorefrontTextfield.svelte';  // Add this import

  export let storefront_hash;
  export let url_preview_text = null;

  $detected_fonts = [];

  // State variables for font preview
  let storefront_data = null;
  let preview_text = "Your Text Here";
  let storefront_name = "";
  let storefront_description = "";
  let font_scale = 3;  // Base font size multiplier
  let sort_order = "ascending";
  let tracking = 0;    // Letter spacing control
  let line_space = 1.25; // Line spacing control
  let bgColor = "ffffff";  // Background color for previews
  let textColor = "000000"; // Text color for previews
  let notFound = false;  // Track if storefront was not found

  let zoomedFontId = null;  // Currently zoomed font ID
  let hoveredFontId = null; // Font ID being hovered
  let copiedFontId = null;  // Font ID that was just copied

  // show business notice
  let showBusinessNotice = false;

  // Apply theme to document body
  document.body.classList.add('light_theme');

  // Create a placeholder font object for loading state
  function createPlaceholderFont(fontData, index) {  
    return {
      id: index,  
      postscript_name: fontData.postscript_name,
      display_name: fontData.postscript_name,
      preferred_name: fontData.preferred_name,
      file_name: fontData.file_name,
      file_url: fontData.file_url,
      loaded: false,
    };
  }

  function isBusinessSubscriptionActive() {
    const subscription = $user_subscription;

    if (!subscription) {
      console.warn('[Subscription Check] No subscription data available');
      return false;
    }

    if (subscription.state === "deactivated") {
      console.warn('[Subscription Check] Subscription is deactivated:', subscription.state);
      return false;
    }

    const isBusiness = subscription.product?.toLowerCase().includes('business');
    console.log('[Subscription Check] Is business subscription:', isBusiness);
    return isBusiness;
  }

  // Load storefront data
  onMount(async () => {
    console.log('[Storefront] Starting to load storefront data for hash:', storefront_hash);
    const data_loaded = await loadStorefrontData(storefront_hash);
    if (data_loaded) {
      try {
        // Set the state variables from storefront data
        storefront_data = data_loaded;
        storefront_name = data_loaded.name;
        storefront_description = data_loaded.description;
        preview_text = url_preview_text || data_loaded.default_text || "Your Text Here";
        font_scale = parseFloat(data_loaded.font_scale) || 3;
        sort_order = data_loaded.sort_order || "ascending";
        tracking = parseFloat(data_loaded.tracking) || 0;
        
        // Use fonts array directly - no JSON.parse needed
        const fonts = data_loaded.fonts || [];
        const startId = $detected_fonts.length;
        const placeholderFonts = fonts.map((font, index) => 
          createPlaceholderFont(font, startId + index)
        );
        $detected_fonts = [...$detected_fonts, ...placeholderFonts];
        
        // Sort fonts - also considering weights
        switch (sort_order) {
          case 'ascending':
            $detected_fonts = sortBy($detected_fonts, [
              font => {
                const name = (font.preferred_name || font.family_name).toLowerCase();
                // Natural sort - split into chunks of text and numbers
                return name.replace(/(\d+)|(\D+)/g, function(_, num, text) {
                  // If it's a number, pad it with leading zeros
                  if (num) return num.padStart(10, '0');
                  return text;
                });
              },
              font => font.weight !== undefined ? font.weight : 400
            ]);
            break;
          case 'descending':
            $detected_fonts = sortBy($detected_fonts, [
              font => {
                const name = (font.preferred_name || font.family_name).toLowerCase();
                // Natural sort - split into chunks of text and numbers
                return name.replace(/(\d+)|(\D+)/g, function(_, num, text) {
                  // If it's a number, pad it with leading zeros
                  if (num) return num.padStart(10, '0');
                  return text;
                });
              },
              font => font.weight !== undefined ? -font.weight : 400
            ]).reverse();
            break;
          case 'random':
            $detected_fonts = shuffle([...$detected_fonts]);
            break;
        }
        console.log('Storefront data and fonts loaded', $detected_fonts);
      } catch (error) {
        console.error('Error processing storefront data:', error);
      }
    }
  });

  async function loadStorefrontData(hash) {
    console.log('[Storefront] Fetching storefront data for hash:', hash);
    try {
      const response = await getStorefrontByHash(hash);
      
      if (response.success) {
        console.log('[Storefront] Successfully loaded storefront data:', {
          storefront_id: response.storefront?.id,
          user_id: response.user?.id,
          subscription_id: response.user?.subscription_id,
          timestamp: new Date().toISOString()
        });

        // Reset not found state since we found the storefront
        notFound = false;

        // Fetch subscription data in the background without blocking
        if (response.user?.subscription_id) {
          console.log('[Subscription] Fetching subscription data for ID:', response.user.subscription_id);
          getUserSubscription(response.user.subscription_id)
            .then(user_subscription_response => {
              if (user_subscription_response?.data) {
                console.log('[Subscription] Received subscription data:', {
                  state: user_subscription_response.data.state,
                  product: user_subscription_response.data.product,
                  subscription_id: user_subscription_response.data.id,
                  timestamp: new Date().toISOString()
                });
                
                user_subscription.set(user_subscription_response.data);
                
                const shouldShowNotice = !isBusinessSubscriptionActive();
                console.log('[Subscription] Business notice status:', {
                  shouldShowNotice,
                  reason: shouldShowNotice ? 'No active business subscription' : 'Active business subscription found'
                });
                
                // showBusinessNotice = shouldShowNotice;
              } else {
                console.error('[Subscription] Invalid subscription response:', user_subscription_response);
                // Don't show watermark on invalid response
                showBusinessNotice = false;
              }
            })
            .catch(error => {
              console.error('[Subscription] Error fetching subscription:', {
                error: error.message,
                subscription_id: response.user.subscription_id,
                timestamp: new Date().toISOString()
              });
              // Don't show watermark on fetch error
              showBusinessNotice = false;
            });
        } else {
          console.warn('[Storefront] No subscription ID found for user:', response.user?.id);
          // Don't show watermark if no subscription ID found
          showBusinessNotice = false;
        }
        return response.storefront;
      }
      
      console.error('[Storefront] Failed to load storefront:', {
        message: response.message,
        hash: hash,
        timestamp: new Date().toISOString()
      });
      return null;
    } catch (error) {
      // Check if it's a 404 error
      if (error.response?.status === 404) {
        console.log('[Storefront] Storefront not found:', hash);
        notFound = true;
      } else {
        console.error('[Storefront] Error loading storefront:', error);
      }
      return null;
    }
  }

  // Handle content edit
  function handleTextChange(event) {
    preview_text = event.detail || "Your Text Here";
  }

  // Toggle zoom state for a font preview
  function toggleZoom(fontId) {
    document.activeElement?.blur();
    
    if (zoomedFontId === fontId) {
      // Zoom out
      zoomedFontId = null;
      document.body.style.overflow = '';
    } else {
      // Zoom in
      zoomedFontId = fontId;
      document.body.style.overflow = 'hidden';
    }
  }

  // Handle font name hover state
  function handleFontNameHover(fontId, isEntering) {
    if (isEntering && copiedFontId !== fontId) {
      hoveredFontId = fontId;
    } else if (!isEntering && copiedFontId !== fontId) {
      hoveredFontId = null;
    }
  }

  // Copy font name to clipboard
  async function copyFontName(event, font) {
    event.stopPropagation(); 
    const fontName = font.preferred_name !== font.display_name 
      ? font.preferred_name 
      : font.display_name;
    
    try {
      await navigator.clipboard.writeText(fontName);
      copiedFontId = font.id;
      setTimeout(() => {
        copiedFontId = null;
        hoveredFontId = null;
      }, 3000);
    } catch (err) {
      console.error('Failed to copy font name:', err);
    }
  }

  // Handle close button click
  function handleCloseClick(event, fontId) {
    event.stopPropagation();
    toggleZoom(fontId);
  }

</script>

<div class="previewsContainer" class:showBusinessNotice>
  {#if storefront_data}
    <h2>{storefront_name}</h2>
    <p>{storefront_description}</p>
    <StorefrontTextfield text={preview_text} on:textChange={handleTextChange} />
    <div class="previews">
      {#each $detected_fonts as font}
        <div 
          class="preview" 
          class:zoomed={zoomedFontId === font.id}
          data-font-id={font.id}
          in:scale={{start: 0.9}}
          on:click={() => toggleZoom(font.id)}
          style="font-size:{Math.pow(1.2, font_scale)}em;">
          <div class="preview-content">
            <StorefrontFontImagePreview 
              font={font}
              previewText={preview_text}
              tracking={tracking}
              lineSpace={line_space}
              fontScale={font_scale}
              bgColor={bgColor}
              textColor={textColor}
            />
          </div>
          {#if zoomedFontId === font.id}
            <div class="floatingCloseButton">
              <Button kind="transparent" size="small" on:click={(e) => handleCloseClick(e, font.id)}>
                Close
                <svg class="icon"><use xlink:href="#icon-times"/></svg>
              </Button>
            </div>
          {/if}
          <div class="preview-info">
            <p 
              class="fontname"
              on:mouseenter={() => handleFontNameHover(font.id, true)}
              on:mouseleave={() => handleFontNameHover(font.id, false)}
              on:click={(e) => copyFontName(e, font)}
            >
              {#if copiedFontId === font.id}
                👍 Font name copied
              {:else if hoveredFontId === font.id}
                Copy font name
              {:else}
                {#if font.preferred_name !== font.display_name}
                  {font.preferred_name}
                {:else}
                  {font.display_name}
                {/if}
              {/if}
            </p>
          </div>
        </div>
      {/each}
    </div>
  {:else if notFound}
    <div class="not-found-container">
      <h2>Storefront Not Found</h2>
      <p>The storefront you're looking for doesn't exist or has been removed.</p>
    </div>
  {:else}
    <div class="loading-container">
      <Blink>
        <p>Loading...</p>
      </Blink>
    </div>
  {/if}
</div>
<Icons />

<style>
  .previewsContainer {
    min-height: 100%;
    background-color: var(--c-bg);
    transition: background-color var(--transition-props);
    color: var(--c-text);
  }

  .previewsContainer > h2 {
    padding-top: 3rem;
    text-align: center;
    white-space: pre-wrap;
    color: var(--c-text);
  }

  .previewsContainer > p {
    padding: 2.5rem 3rem 3rem;
    text-align: center;
    font-size: 1.6rem;
    white-space: pre-wrap;
  }

  .previews {
    position: relative;
    padding: 1rem;
    display: flex;
    flex-flow: row wrap;
    text-align: center;
    font-size: 20px;
    background-color: var(--c-bg);
    transition: background-color var(--transition-props);
  }

  .preview {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 300px;
    max-width: 100%;
    overflow: hidden;
    margin: 1rem;
    text-align: center;
    color: var(--c-text);
    background-color: var(--c-content-bg);
    transition-property: background-color, border-width, transform;
    transition-duration: var(--transition-duration);
    transition-timing-function: var(--transition-function);
    cursor: zoom-in;
    z-index: 1;
  }

  .preview.zoomed {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    max-width: 100%;
    margin: 0;
    padding: 2em;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2rem solid var(--c-bg);
    cursor: zoom-out;
  }

  .preview.zoomed .preview-info {
    position: absolute;
    line-height: 8rem;
  }

  .preview.zoomed .preview-content {
    margin-bottom: 8rem;
  }

  .preview:not(.zoomed):hover {
    transform: scale(1.02);
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 240px;
    font-family: var(--PensumSans);
    color: var(--c-text);
  }

  .preview-content {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    width: 100%;
  }

  .preview-info {
    bottom: 0;
    width: 100%;
    text-align: center;
    font-family: var(--PensumSans);
    font-size: 1.4rem;
    line-height: 4rem;
    letter-spacing: 0.02rem;
    color: var(--c-text);
    background-color: var(--c-textfield-bg);
  }

  .preview-info .fontname {
    cursor: pointer;
    margin: 0;
    transition: color var(--transition-duration) var(--transition-function);
  }

  .preview-info .fontname:hover {
    color: var(--c-accent);
    background-color: var(--c-humane-bg);
  }
  
  .floatingCloseButton {
    position: absolute;
    z-index: 1001; /* Above the zoomed preview */
    top: 1rem;
    right: 0;
  }

  .floatingCloseButton :global(svg.icon) {
    vertical-align: middle;
    margin-left: -0.5rem;
  }

  .previewsContainer.showBusinessNotice {
    position: relative;
  }

  .previewsContainer.showBusinessNotice::after {
    content: "This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - This is your storefront preview. Upgrade to remove the watermark before sharing  - ";
    position: absolute;
    z-index: 10000;
    top: 0;
    left: 0;
    width: calc(100% - 1rem);
    height: calc(100% - 1rem);
    margin: 0.5rem;
    padding: 0 1rem;
    opacity: 0.15;
    font-size: 2.8rem;
    pointer-events: none;
    overflow: hidden;
    line-height: 1.5em;
    color: var(--c-accent-orange);
    border: 1rem dotted;
  }

  .not-found-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 240px;
    text-align: center;
    padding: 2rem;
  }

  .not-found-container h2 {
    font-family: var(--PensumSans);
    color: var(--c-text);
    margin-bottom: 1rem;
  }

  .not-found-container p {
    font-family: var(--PensumSans);
    color: var(--c-text);
    opacity: 0.8;
  }

</style>